import { InterviewSectionType } from "@repo/app-types/entities/interviewSection/types";
import { assertUnreachable } from "@repo/app-types/helpers/utilities";
import type { VisibleInterviewSectionDefinition } from "./state.js";

// Duration constants in minutes per question
const KNOWLEDGE_TEST_MIN_MINUTES_PER_QUESTION = 0.75;
const KNOWLEDGE_TEST_MAX_MINUTES_PER_QUESTION = 1.25;
const CHAT_QUIZ_MIN_MINUTES_PER_QUESTION = 0.25;
const CHAT_QUIZ_MAX_MINUTES_PER_QUESTION = 0.75;

// Fixed duration constants in minutes
const QUESTIONS_MIN_DURATION = 5;
const QUESTIONS_MAX_DURATION = 15;
const ROLE_PLAY_MIN_DURATION = 6;
const ROLE_PLAY_MAX_DURATION = 12;
const DEFAULT_MIN_DURATION = 10;
const DEFAULT_MAX_DURATION = 15;

export function computeInterviewSectionEstimatedDuration(
  section: VisibleInterviewSectionDefinition,
): [number, number] {
  switch (section.settings.type) {
    case InterviewSectionType.KnowledgeTest: {
      const questionCount = section.settings.questions.filter(
        ({ question }) => !!question,
      ).length;

      return computeEstimatedDurationFromQuestionCount(
        questionCount,
        KNOWLEDGE_TEST_MIN_MINUTES_PER_QUESTION,
        KNOWLEDGE_TEST_MAX_MINUTES_PER_QUESTION,
      );
    }
    case InterviewSectionType.ChatQuiz: {
      const questionCount = section.settings.questions.filter(
        ({ question }) => !!question,
      ).length;

      return computeEstimatedDurationFromQuestionCount(
        questionCount,
        CHAT_QUIZ_MIN_MINUTES_PER_QUESTION,
        CHAT_QUIZ_MAX_MINUTES_PER_QUESTION,
      );
    }
    case InterviewSectionType.Questions:
      // TODO: Use the question count to determine the duration
      return [QUESTIONS_MIN_DURATION, QUESTIONS_MAX_DURATION];

    case InterviewSectionType.RolePlay:
      return [ROLE_PLAY_MIN_DURATION, ROLE_PLAY_MAX_DURATION];

    default:
      return assertUnreachable(section.settings, () => [
        DEFAULT_MIN_DURATION,
        DEFAULT_MAX_DURATION,
      ]);
  }
}

function computeEstimatedDurationFromQuestionCount(
  questionCount: number,
  minMinutesPerQuestion: number,
  maxMinutesPerQuestion: number,
): [number, number] {
  return [
    Math.max(1, Math.round(questionCount * minMinutesPerQuestion)),
    Math.max(1, Math.round(questionCount * maxMinutesPerQuestion)),
  ];
}

export function renderInterviewSectionBlurb(
  section: VisibleInterviewSectionDefinition,
  questionCount: number | undefined,
) {
  switch (section.settings.type) {
    case InterviewSectionType.KnowledgeTest:
      return section.settings.title ?? "Knowledge test";

    case InterviewSectionType.ChatQuiz:
      return section.settings.topic;

    case InterviewSectionType.Questions: {
      if (questionCount !== undefined) {
        const questions =
          questionCount === 1 ? "1 question" : `${questionCount} questions`;

        return `Screening interview with ${questions}`;
      }

      return "Screening interview";
    }
    case InterviewSectionType.RolePlay:
      return section.settings.title ?? "Role play";

    default:
      return assertUnreachable(section.settings, () => "Unknown section");
  }
}
